import { apiService } from './ApiService';
import {
  IProviderFeeTerms,
  IProviderFeeTermsList,
  ISetProviderFeeTermsRequest,
} from '../interfaces/heavyDutyMachinery/IProviderFeeTerms';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { HTTP_METHODS } from '../constants/httpMethods';
import { getLocalStorageKey } from '../helpers/localStorageHelper';
import { IHostProfile, IUpdateHostProfileRequest } from '../interfaces/heavyDutyMachinery/IHostProfile';
import {
  IAdminConversationSummary,
  IAdminConversationMessage,
  IAdminConversationsPage,
  IAdminConversationMessagesPage,
} from '../interfaces/heavyDutyMachinery/IAdminConversation';

const heavyDutyMachineryBaseUrl = process.env.REACT_APP_HEAVY_DUTY_MACHINERY_API_URL || '';

export interface IAdminSession {
  role: string;
  isAdmin: boolean;
  isModerator: boolean;
}

export interface IAdminAuditLogEntry {
  id: string;
  adminName: string;
  action: string;
  targetLabel: string;
  reason?: string;
  createdAt: string;
}

// Called once when the admin panel loads -- a defense-in-depth check
// alongside the frontend's own AppRole-based gate (see AuthProvider/
// ProtectedRoute): confirms the backend itself agrees the caller is
// Admin or Moderator (it enforces this independently via
// [Authorize(Roles = ...)] on every /api/heavy-duty-machinery/admin/* route).
export const getAdminSession = async (): Promise<IAdminSession> => {
  const response = await apiService<IAdminSession>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_SESSION,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load admin session');
};

// Immutable -- every other admin action writes here server-side
// (AdminAuditLogHelper), there is no create/edit/delete from this UI.
export const getAdminAuditLog = async (): Promise<IAdminAuditLogEntry[]> => {
  const response = await apiService<IAdminAuditLogEntry[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_AUDIT_LOG,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the admin audit log');
};

export interface IErpSyncOutboxEntry {
  id: number;
  hostUserId: string;
  eventType: string;
  attempts: number;
  lastErrorMessage?: string;
  createdAt: string;
}

// Read-only -- rows that exhausted ErpSyncBackgroundService's retries
// (Status=Failed, NextRetryAt=null) and stopped being picked up
// automatically. No retry action from this UI yet.
export const getExhaustedErpSyncEntries = async (): Promise<IErpSyncOutboxEntry[]> => {
  const response = await apiService<IErpSyncOutboxEntry[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_ERP_SYNC_EXHAUSTED,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the exhausted ERP sync entries');
};

export interface IFeatureFlag {
  key: string;
  enabled: boolean;
  updatedAt?: string;
  updatedByUserId?: string;
}

// 404 means nobody has ever set this key yet (see AdminFeatureFlagsController.
// GetFlag's own "$'No feature flag with key...has been set yet'" body) --
// matched by that message text since apiService surfaces the backend's own
// JSON `message` here, never a bare HTTP status. The caller treats a null
// return as "off, never configured" rather than an error.
export const getFeatureFlag = async (key: string): Promise<IFeatureFlag | null> => {
  const response = await apiService<IFeatureFlag>({
    url: API_ENDPOINTS.ADMIN_FEATURE_FLAG_BY_KEY(key),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  if (response.error?.toLowerCase().includes('has been set yet')) return null;
  throw new Error(response.error || 'Failed to load the feature flag');
};

// Upserts -- a key nobody has set before is created by this same call, see
// AdminFeatureFlagsController.UpdateFlag's own doc comment.
export const updateFeatureFlag = async (key: string, enabled: boolean): Promise<IFeatureFlag> => {
  const response = await apiService<IFeatureFlag>({
    url: API_ENDPOINTS.ADMIN_FEATURE_FLAG_BY_KEY(key),
    method: HTTP_METHODS.PUT,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { enabled },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to update the feature flag');
};

export interface IAdminBookingLine {
  id: string;
  bookingId: string;
  lineType: string;
  title: string;
  realProviderUserId: string;
  providerName: string;
  amount: number;
  hostCurrencyCode: string;
  paymentStatus: string;
  transferAttempts: number;
  transferLastErrorMessage?: string;
  lastTransferAttemptAt?: string;
}

// status defaults server-side to PendingResolution -- the one status an
// admin actually needs to act on, see AdminFinanceController.GetBookingLines'
// own doc comment.
export const getAdminBookingLines = async (status?: string): Promise<IAdminBookingLine[]> => {
  const response = await apiService<IAdminBookingLine[]>({
    url: `${API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_FINANCE_BOOKING_LINES}${status ? `?status=${status}` : ''}`,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the booking lines');
};

// Forces an immediate retry of this line's own transfer group -- see
// AdminFinanceController.RetryBookingLine's own doc comment. Returns the
// line's own fresh state (Succeeded, or still Pending/PendingResolution
// with an updated TransferLastErrorMessage) for the caller to display.
export const retryAdminBookingLine = async (id: string): Promise<IAdminBookingLine> => {
  const response = await apiService<IAdminBookingLine>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_FINANCE_BOOKING_LINE_RETRY(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to retry the booking line transfer');
};

// Marks this line's own transfer group ManuallyResolved -- requires a
// reason, recorded in the audit log (see AdminFinanceController.
// ResolveBookingLine's own doc comment).
export const resolveAdminBookingLine = async (id: string, reason: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_FINANCE_BOOKING_LINE_RESOLVE(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { reason },
  });
  if (!response.success) throw new Error(response.error || 'Failed to resolve the booking line');
};

// Read from the section header's global switch (SpacesSection) -- controls
// whether HeavyDutyMachineryController.PublishSpace self-serves straight to
// Active or parks the space in PendingVerification for this queue instead.
export interface IAdminPlatformSettings {
  requireManualSpaceApproval: boolean;
  /**
   * Whether a listing may still take an immediate-payment booking at all.
   *
   * Off, EVERY listing behaves as reserva diferida regardless of its own
   * switch: checkout stops at a quote request the host has to answer, and no
   * card is charged until somebody accepts a price. It is the one lever that
   * turns the whole marketplace quote-only without editing machines one by
   * one -- which is what you want on the day something is wrong with
   * automatic pricing, not a week of edits.
   */
  immediateBookingEnabled: boolean;

  /** Whether hosts must have a deposit before taking bookings that charge up front. */
  hostDepositRequired: boolean;

  /**
   * What the payment processor charges per transaction, as a percentage.
   *
   * The single most consequential number on this screen: every host's
   * booking ceiling is derived from it, and it is also what a customer who
   * cancels is charged. Set it from the real Stripe tariff -- guessing low
   * means deposits that quietly cover less than they claim to.
   */
  processingFeePercent: number;
  /** The fixed part of that same fee, per transaction. */
  processingFeeFixed: number;

  /** Whether hosts may offer charge-at-handover at all. Off means none of them can see or use it. */
  deferredCaptureEnabled: boolean;
  /** Days before a rental starts that the funds are frozen, on a charge-at-handover booking. 1-6: a card hold only lives about seven days. */
  deferredCaptureLeadDays: number;
}

export const getPlatformSettings = async (): Promise<IAdminPlatformSettings> => {
  const response = await apiService<IAdminPlatformSettings>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_SETTINGS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the platform settings');
};

/**
 * Saves the WHOLE settings object, never a single field.
 *
 * The endpoint is a PUT over the complete record, so anything left out of
 * the body falls back to the request DTO's own default. While this only ever
 * sent requireManualSpaceApproval, every save of that toggle silently forced
 * immediateBookingEnabled back to true -- the platform-wide quote-only lever
 * could be switched off and would turn itself back on the next time somebody
 * touched an unrelated switch on the same screen. Callers pass the full
 * settings they intend to end up with.
 */
export const updatePlatformSettings = async (settings: IAdminPlatformSettings): Promise<IAdminPlatformSettings> => {
  const response = await apiService<IAdminPlatformSettings>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_SETTINGS,
    method: HTTP_METHODS.PUT,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { ...settings },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to update the platform settings');
};

export interface IAdminSpaceSummary {
  id: string;
  hostUserId: string;
  hostName: string;
  title: string;
  city: string;
  country: string;
  status: string;
  verifiedBadge: boolean;
  suspensionReason?: string;
  bookingCount: number;
  createdAt: string;
  publishRequestedAt?: string;
}

export interface IAdminSpaceAudit {
  id: string;
  score: number;
  badgeGranted: boolean;
  notes?: string;
  createdAt: string;
  disclosedToHostAt?: string;
}

export interface IAdminSpaceDetail extends IAdminSpaceSummary {
  hostEmail: string;
  hostPhone: string;
  description: string;
  formattedAddress: string;
  privateAddress: string;
  rentalMode: string;
  pricePerHour?: number;
  pricePerNight?: number;
  pricePerDayPass?: number;
  verifiedBadgeExpiresAt?: string;
  improvementDeadline?: string;
  photoThumbnails: string[];
  audits: IAdminSpaceAudit[];
}

export const getAdminSpaces = async (status?: string, search?: string): Promise<IAdminSpaceSummary[]> => {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  if (search) params.search = search;

  const response = await apiService<IAdminSpaceSummary[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_SPACES,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load spaces');
};

export const getAdminSpace = async (id: string): Promise<IAdminSpaceDetail> => {
  const response = await apiService<IAdminSpaceDetail>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_SPACE_BY_ID(id),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the space');
};

export const approveAdminSpace = async (id: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_SPACE_APPROVE(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to approve the space');
};

export const rejectAdminSpace = async (id: string, reason: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_SPACE_REJECT(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { reason },
  });
  if (!response.success) throw new Error(response.error || 'Failed to reject the space');
};

export const unpublishAdminSpace = async (id: string, reason: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_SPACE_UNPUBLISH(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { reason },
  });
  if (!response.success) throw new Error(response.error || 'Failed to unpublish the space');
};

export const reactivateAdminSpace = async (id: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_SPACE_REACTIVATE(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to reactivate the space');
};

export interface IAdminUserSummary {
  userId: string;
  displayName: string;
  email: string;
  role: string;
  isSuspended: boolean;
  suspendedReason?: string;
  createdAt: string;
}

export interface IAdminUserBooking {
  id: string;
  heavyDutyMachineryTitle: string;
  startAt: string;
  endAt: string;
  status: string;
  totalPrice: number;
}

export interface IAdminUserDetail extends IAdminUserSummary {
  phone: string;
  country: string;
  suspendedAt?: string;
  recentBookings: IAdminUserBooking[];
}

export const getAdminUsers = async (role?: string, isSuspended?: boolean, search?: string): Promise<IAdminUserSummary[]> => {
  const params: Record<string, string> = {};
  if (role) params.role = role;
  if (isSuspended !== undefined) params.isSuspended = String(isSuspended);
  if (search) params.search = search;

  const response = await apiService<IAdminUserSummary[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_USERS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load users');
};

export const getAdminUser = async (id: string): Promise<IAdminUserDetail> => {
  const response = await apiService<IAdminUserDetail>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_USER_BY_ID(id),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the user');
};

export const suspendAdminUser = async (id: string, reason: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_USER_SUSPEND(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { reason },
  });
  if (!response.success) throw new Error(response.error || 'Failed to suspend the user');
};

export const reactivateAdminUser = async (id: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_USER_REACTIVATE(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to reactivate the user');
};

export interface IAdminRoleUser {
  userId: string;
  displayName: string;
  email: string;
  role: string;
  createdAt: string;
}

// Admin-only endpoint (see AdminRolesController) -- a Moderator's token
// gets a 403 here even if they somehow reach this code path, since
// Admin.tsx's own ADMIN_ONLY_SECTIONS already keeps the section itself
// out of their sidebar.
export const getAdminRoleUsers = async (search?: string): Promise<IAdminRoleUser[]> => {
  const params: Record<string, string> = {};
  if (search) params.search = search;

  const response = await apiService<IAdminRoleUser[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_ROLES,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load users');
};

export const setAdminUserRole = async (userId: string, role: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_ROLE_SET(userId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { role },
  });
  if (!response.success) throw new Error(response.error || 'Failed to update the role');
};

export interface IAdminAgeVerificationUser {
  userId: string;
  displayName: string;
  email: string;
  ageVerified: boolean;
  ageVerifiedAt?: string;
  createdAt: string;
}

export const getAdminAgeVerificationUsers = async (verified?: boolean, search?: string): Promise<IAdminAgeVerificationUser[]> => {
  const params: Record<string, string> = {};
  if (verified !== undefined) params.verified = String(verified);
  if (search) params.search = search;

  const response = await apiService<IAdminAgeVerificationUser[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_AGE_VERIFICATION,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load users');
};

export const overrideAdminAgeVerification = async (userId: string, verified: boolean, reason: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_AGE_VERIFICATION_OVERRIDE(userId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { verified, reason },
  });
  if (!response.success) throw new Error(response.error || 'Failed to update age verification');
};

export interface IAdminBookingSummary {
  id: string;

  /** The reference shown on every screen the host and the guest see, e.g. "R-PPTAR" -- so a support call that starts with one lands on the right row here. */
  reference: string;

  heavyDutyMachineryId: string;
  heavyDutyMachineryTitle: string;
  hostUserId: string;
  hostName: string;
  guestUserId: string;
  guestName: string;
  startAt: string;
  endAt: string;
  rentalMode: string;
  totalPrice: number;
  status: string;
  paymentStatus: string;
  /** Immediate | OnAcceptance | OnHandover -- the same booking behaves completely differently depending on this. */
  captureMode: string;
  /** Server-computed, shared with the money trail so the two can never disagree. */
  moneyLocation: AdminMoneyLocation;
  depositStatus: string;
  refundedAmount: number;
  createdAt: string;
}

export interface IAdminBookingDetail extends IAdminBookingSummary {
  paymentReference?: string;
  hostPayoutAmount?: number;
  isAuditBooking: boolean;
  liabilityWaiverAcceptedAt?: string;
}

export const getAdminBookings = async (status?: string, search?: string, captureMode?: string): Promise<IAdminBookingSummary[]> => {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  if (search) params.search = search;
  if (captureMode) params.captureMode = captureMode;

  const response = await apiService<IAdminBookingSummary[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_BOOKINGS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load bookings');
};

export const getAdminBooking = async (id: string): Promise<IAdminBookingDetail> => {
  const response = await apiService<IAdminBookingDetail>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_BOOKING_BY_ID(id),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the booking');
};

export const forceCancelAdminBooking = async (id: string, reason: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_BOOKING_FORCE_CANCEL(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { reason },
  });
  if (!response.success) throw new Error(response.error || 'Failed to cancel the booking');
};

export interface IAdminFinanceSummary {
  totalRevenue: number;
  platformFees: number;
  hostPayouts: number;
  totalRefunded: number;
  transactionCount: number;
}

export interface IAdminFinanceTransaction {
  id: string;
  heavyDutyMachineryTitle: string;
  hostName: string;
  guestName: string;
  totalPrice: number;
  platformFee?: number;
  hostPayoutAmount?: number;
  paymentStatus: string;
  paymentReference?: string;
  createdAt: string;
}

export const getAdminFinanceSummary = async (): Promise<IAdminFinanceSummary> => {
  const response = await apiService<IAdminFinanceSummary>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_FINANCE_SUMMARY,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the finance summary');
};

export const getAdminFinanceTransactions = async (status?: string): Promise<IAdminFinanceTransaction[]> => {
  const params: Record<string, string> = {};
  if (status) params.status = status;

  const response = await apiService<IAdminFinanceTransaction[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_FINANCE_TRANSACTIONS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the transactions');
};

export interface IAdminOvertimeCharge {
  bookingId: string;
  heavyDutyMachineryId: string;
  heavyDutyMachineryTitle: string;
  hostName: string;
  guestName: string;
  overtimeAmountBilled: number;
  overtimeChargeStatus: string;
  endAt: string;
}

export const getAdminOvertimeCharges = async (): Promise<IAdminOvertimeCharge[]> => {
  const response = await apiService<IAdminOvertimeCharge[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_FINANCE_OVERTIME_CHARGES,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the overtime charges');
};

export interface IAdminReview {
  id: string;
  heavyDutyMachineryId: string;
  heavyDutyMachineryTitle: string;
  guestUserId: string;
  guestName: string;
  rating: number;
  comment?: string;
  hostResponse?: string;
  isHidden: boolean;
  hiddenReason?: string;
  createdAt: string;
}

export const getAdminReviews = async (hidden?: boolean, search?: string): Promise<IAdminReview[]> => {
  const params: Record<string, string> = {};
  if (hidden !== undefined) params.hidden = String(hidden);
  if (search) params.search = search;

  const response = await apiService<IAdminReview[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_REVIEWS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load reviews');
};

export const hideAdminReview = async (id: string, reason: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_REVIEW_HIDE(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { reason },
  });
  if (!response.success) throw new Error(response.error || 'Failed to hide the review');
};

export const unhideAdminReview = async (id: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_REVIEW_UNHIDE(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to unhide the review');
};

export const deleteAdminReview = async (id: string, reason: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_REVIEW_DELETE(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { reason },
  });
  if (!response.success) throw new Error(response.error || 'Failed to delete the review');
};

/**
 * Grants or withdraws a machine's "Verificado" badge by hand, with a
 * recorded reason. Its route still lives under the old quality-audits
 * controller (the badge used to be a by-product of an audit); the audit
 * flow itself is gone from the panel, the badge is not -- Máquinas owns it
 * now (see SpacesSection's detail drawer).
 */
export const overrideVerifiedBadge = async (
  spaceId: string,
  verifiedBadge: boolean,
  reason: string,
  verifiedBadgeExpiresAt?: string
): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_QUALITY_AUDIT_OVERRIDE_BADGE(spaceId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { verifiedBadge, reason, verifiedBadgeExpiresAt },
  });
  if (!response.success) throw new Error(response.error || 'Failed to update the badge');
};

export interface IAllowedCountry {
  id: string;
  countryCode: string;
  countryName: string;
  createdAt: string;
  lastSyncedAt: string | null;
}

export const getAllowedCountries = async (): Promise<IAllowedCountry[]> => {
  const response = await apiService<IAllowedCountry[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_GEO_BLOCKING,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load allowed countries');
};

export const addAllowedCountry = async (countryCode: string, countryName: string): Promise<IAllowedCountry> => {
  const response = await apiService<IAllowedCountry>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_GEO_BLOCKING,
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { countryCode, countryName },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to allow the country');
};

export const removeAllowedCountry = async (id: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_GEO_BLOCKING_BY_ID(id),
    method: HTTP_METHODS.DELETE,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to remove the country');
};

export const refreshAllowedCountry = async (id: string): Promise<IAllowedCountry> => {
  const response = await apiService<IAllowedCountry>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_GEO_BLOCKING_REFRESH(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to refresh the country');
};

export const refreshAllAllowedCountries = async (): Promise<IAllowedCountry[]> => {
  const response = await apiService<IAllowedCountry[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_GEO_BLOCKING_REFRESH_ALL,
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to refresh the allowed countries');
};

export interface IAdminDashboardSummary {
  activeUsers: number;
  hosts: number;
  publishedSpaces: number;
  bookingsThisMonth: number;
  unresolvedReports: number;

  /** Charged and still on the platform's own balance: money held, not money earned. */
  inEscrow: number;
  inEscrowCount: number;
  /** Guests' fianzas frozen on cards. Never the platform's money. */
  depositsHeld: number;
  depositsHeldCount: number;
  /** Live rentals whose hold already lapsed -- machines out there with nothing behind them. */
  depositsExpiredWhileRunning: number;
  hostDepositsBalance: number;
  hostDepositsLowCount: number;
  platformFeesThisMonth: number;
  /** Money a payee should have received and hasn't. */
  stuckTransfers: number;
  stuckTransfersCount: number;
}

export const getAdminDashboardSummary = async (): Promise<IAdminDashboardSummary> => {
  const response = await apiService<IAdminDashboardSummary>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_DASHBOARD_SUMMARY,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the dashboard summary');
};

export interface IAdminHostSummary {
  userId: string;
  displayName: string;
  accountType: string;
  legalName: string;
  idVerificationStatus: string;
  profileStatus: string;
  verifiedHostBadge: boolean;
  createdAt: string;
}

export const getAdminHosts = async (accountType?: string, idVerificationStatus?: string): Promise<IAdminHostSummary[]> => {
  const params: Record<string, string> = {};
  if (accountType) params.accountType = accountType;
  if (idVerificationStatus) params.idVerificationStatus = idVerificationStatus;

  const response = await apiService<IAdminHostSummary[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_HOSTS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load hosts');
};

export const getAdminHost = async (userId: string): Promise<IHostProfile> => {
  const response = await apiService<IHostProfile>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_HOST_BY_ID(userId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the host profile');
};

export const updateAdminHost = async (userId: string, request: IUpdateHostProfileRequest): Promise<IHostProfile> => {
  const response = await apiService<IHostProfile>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_HOST_BY_ID(userId),
    method: HTTP_METHODS.PATCH,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { ...request },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to update the host profile');
};

export const forceHostVerificationStatus = async (userId: string, status: string, reason: string): Promise<IHostProfile> => {
  const response = await apiService<IHostProfile>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_HOST_VERIFICATION(userId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { status, reason },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to update the verification status');
};

export interface IAdminReport {
  id: string;
  targetType: string;
  targetId: string;
  reporterUserId: string;
  reporterName: string;
  reason: string;
  status: string;
  actionTaken?: string;
  wasAppealed: boolean;
  appealNotes?: string;
  createdAt: string;
  resolvedAt?: string;
  resolvedByAdminUserId?: string;
}

export const getAdminReports = async (status?: string, targetType?: string): Promise<IAdminReport[]> => {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  if (targetType) params.targetType = targetType;

  const response = await apiService<IAdminReport[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_REPORTS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load reports');
};

export const resolveAdminReport = async (id: string, actionTaken: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_REPORT_RESOLVE(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { actionTaken },
  });
  if (!response.success) throw new Error(response.error || 'Failed to resolve the report');
};

export const dismissAdminReport = async (id: string, reason: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_REPORT_DISMISS(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { reason },
  });
  if (!response.success) throw new Error(response.error || 'Failed to dismiss the report');
};

export const recordReportAppeal = async (id: string, appealNotes: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_REPORT_APPEAL(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { appealNotes },
  });
  if (!response.success) throw new Error(response.error || 'Failed to record the appeal');
};

export interface IAdminPhoto {
  id: string;
  heavyDutyMachineryId: string;
  heavyDutyMachineryTitle: string;
  url: string;
  thumbnailUrl: string;
  moderationStatus: string;
}

export const getPhotoModerationQueue = async (): Promise<IAdminPhoto[]> => {
  const response = await apiService<IAdminPhoto[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_PHOTO_MODERATION_QUEUE,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the photo moderation queue');
};

export const getSpacePhotosForModeration = async (spaceId: string): Promise<IAdminPhoto[]> => {
  const response = await apiService<IAdminPhoto[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_PHOTO_MODERATION_SPACE_PHOTOS(spaceId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the space photos');
};

export const flagPhotoForModeration = async (photoId: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_PHOTO_MODERATION_FLAG(photoId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to flag the photo');
};

export const approvePhotoModeration = async (photoId: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_PHOTO_MODERATION_APPROVE(photoId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to approve the photo');
};

export const rejectPhotoModeration = async (photoId: string, reason: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_PHOTO_MODERATION_REJECT(photoId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { reason },
  });
  if (!response.success) throw new Error(response.error || 'Failed to reject the photo');
};

export interface IAdminAnnouncement {
  id: string;
  title: string;
  body: string;
  targetSegment: string;
  recipientCount: number;
  sentByAdminName: string;
  createdAt: string;
}

export const getAdminAnnouncements = async (): Promise<IAdminAnnouncement[]> => {
  const response = await apiService<IAdminAnnouncement[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_ANNOUNCEMENTS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load announcements');
};

export const sendAdminAnnouncement = async (
  title: string,
  body: string,
  targetSegment: string
): Promise<IAdminAnnouncement> => {
  const response = await apiService<IAdminAnnouncement>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_ANNOUNCEMENTS,
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { title, body, targetSegment },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to send the announcement');
};

export interface IAdminGdprRequest {
  id: string;
  userDisplayName: string;
  requestType: string;
  status: string;
  createdAt: string;
  processedAt?: string;
  processedByAdminName?: string;
}

export const getAdminGdprRequests = async (status?: string): Promise<IAdminGdprRequest[]> => {
  const params: Record<string, string> = {};
  if (status) params.status = status;

  const response = await apiService<IAdminGdprRequest[]>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_GDPR_REQUESTS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load GDPR requests');
};

// Admin panel's "Mensajes" section -- see AdminConversationsController on
// the backend. Shared inbox: any Admin/Moderator can open/reply/close, no
// per-admin assignment.
export const getAdminConversations = async (status?: string): Promise<IAdminConversationSummary[]> => {
  const params: Record<string, string> = {};
  if (status) params.status = status;
  const response = await apiService<IAdminConversationsPage>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CONVERSATIONS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
    params,
  });
  if (response.success) return response.data.items;
  throw new Error(response.error || 'Failed to load conversations');
};

/** null (not thrown) when this user has never been contacted -- the normal case from a "Contactar" button that hasn't opened one yet. */
export const getAdminConversationByUser = async (userId: string): Promise<IAdminConversationSummary | null> => {
  const response = await apiService<IAdminConversationSummary>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CONVERSATION_BY_USER(userId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  if (response.error === 'Conversation not found') return null;
  throw new Error(response.error || 'Failed to load conversation');
};

export const getAdminConversationMessages = async (conversationId: string, before?: number): Promise<IAdminConversationMessagesPage> => {
  const params: Record<string, string | number> = {};
  if (before != null) params.before = before;
  const response = await apiService<IAdminConversationMessagesPage>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CONVERSATION_MESSAGES(conversationId),
    method: HTTP_METHODS.GET,
    params,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load messages');
};

/** Opens a new conversation with userId, or reopens/appends to their existing one -- see AdminConversationsController.OpenOrReplyConversation. */
export const openAdminConversation = async (
  userId: string,
  text: string,
  subject?: string,
  relatedSpaceId?: string,
  relatedReportId?: string
): Promise<IAdminConversationSummary> => {
  const response = await apiService<IAdminConversationSummary>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CONVERSATIONS,
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { userId, text, subject, relatedSpaceId, relatedReportId },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to open the conversation');
};

export const sendAdminConversationMessage = async (conversationId: string, text: string): Promise<IAdminConversationMessage> => {
  const response = await apiService<IAdminConversationMessage>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CONVERSATION_MESSAGES(conversationId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { text },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to send message');
};

export const closeAdminConversation = async (conversationId: string): Promise<IAdminConversationSummary> => {
  const response = await apiService<IAdminConversationSummary>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CONVERSATION_CLOSE(conversationId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to close the conversation');
};

export const reopenAdminConversation = async (conversationId: string): Promise<IAdminConversationSummary> => {
  const response = await apiService<IAdminConversationSummary>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CONVERSATION_REOPEN(conversationId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to reopen the conversation');
};

/** Count of conversations with at least one unread-from-user message -- powers the Mensajes sidebar badge (see AdminConversationsController.GetUnreadCount). */
export const getAdminConversationsUnreadCount = async (): Promise<number> => {
  const response = await apiService<number>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CONVERSATIONS_UNREAD_COUNT,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the unread count');
};

/**
 * One row of the reviewer's queue -- see AdminDisputeResponse on the
 * backend for why this is deliberately not the whole dispute: a queue is
 * for deciding what to open next, not for deciding the claim.
 */
export interface IAdminDispute {
  id: string;
  bookingId: string;
  machineryTitle: string;
  claimantUserId: string;
  respondentUserId: string;
  reason: string;
  response?: string;
  claimedAmount: number;
  currencyCode: string;
  status: string;
  outcome?: string;
  resolvedAmount?: number;
  /** When resolvedAmount actually moved. Undefined on a closed claim means it did not -- see settlementError. */
  settledAt?: string;
  /** Why it did not move. The reason this column exists at all: a claim decided and not paid used to look exactly like one that was. */
  settlementError?: string;
  autoResolveThreshold: number;
  offerCount: number;
  evidenceCount: number;
  daysOpen: number;
  createdAt: string;
}

// status defaults server-side to UnderReview -- the only queue that needs
// anybody, see AdminDisputesController.GetDisputes' own doc comment.
export const getAdminDisputes = async (status?: string): Promise<IAdminDispute[]> => {
  const response = await apiService<IAdminDispute[]>({
    url: `${API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_DISPUTES}${status ? `?status=${status}` : ''}`,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the claims queue');
};

/**
 * A reviewer's decision on one claim. Lives here rather than in
 * DisputeService because only a reviewer can call it, but it posts to the
 * booking's own dispute endpoint -- the decision belongs next to the
 * booking's evidence, not to a queue.
 */
export const resolveAdminDispute = async (bookingId: string, resolvedAmount: number, reason: string): Promise<void> => {
  const response = await apiService<unknown>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_BOOKING_DISPUTE_RESOLVE(bookingId),
    method: HTTP_METHODS.POST,
    body: { resolvedAmount, reason },
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to resolve the claim');
};

// =============================================================================
// Operarios
//
// A register, not a verification queue: the platform doesn't check licences
// or PRL training (see AdminOperatorsController's own doc comment). The only
// verdict here is commercial -- carry this person, or stop carrying them.
// =============================================================================

export interface IAdminOperatorSummary {
  id: string;
  hostUserId: string;
  hostName: string;
  fullName: string;
  qualifiedCategories: string;
  /** The host's own on/off switch for their own staff. */
  isActive: boolean;
  suspendedAt?: string;
  suspensionReason?: string;
  /** Server-computed: exactly the gate the booking path applies, never re-derived here. */
  isOfferable: boolean;
  linkedMachineryCount: number;
  documentCount: number;
  createdAt: string;
}

export interface IAdminOperatorDocument {
  id: string;
  documentType: string;
  uploadedAt: string;
}

export interface IAdminOperatorDetail extends IAdminOperatorSummary {
  email: string;
  phone?: string;
  hourlyRate: number;
  linkedMachineryTitles: string[];
  completedBookingCount: number;
  upcomingBookingCount: number;
  documents: IAdminOperatorDocument[];
}

/** state: offerable | suspended | inactive | unlinked */
export const getAdminOperators = async (state?: string, search?: string): Promise<IAdminOperatorSummary[]> => {
  const params = new URLSearchParams();
  if (state) params.append('state', state);
  if (search) params.append('search', search);
  const query = params.toString();
  const response = await apiService<IAdminOperatorSummary[]>({
    url: `${API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_OPERATORS}${query ? `?${query}` : ''}`,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the operators');
};

export const getAdminOperator = async (id: string): Promise<IAdminOperatorDetail> => {
  const response = await apiService<IAdminOperatorDetail>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_OPERATOR_BY_ID(id),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the operator');
};

export const suspendAdminOperator = async (id: string, reason: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_OPERATOR_SUSPEND(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { reason },
  });
  if (!response.success) throw new Error(response.error || 'Failed to suspend the operator');
};

export const reinstateAdminOperator = async (id: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_OPERATOR_REINSTATE(id),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to reinstate the operator');
};

/**
 * Same raw-fetch + object-URL dance as OperatorService.downloadOperatorDocument
 * (apiService can't carry a binary body). The server records every one of
 * these in the admin audit log -- see that endpoint's own doc comment.
 */
export const downloadAdminOperatorDocument = async (operatorId: string, doc: IAdminOperatorDocument): Promise<void> => {
  const accessToken = getLocalStorageKey('accessToken');
  const url = API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_OPERATOR_DOCUMENT_DOWNLOAD(operatorId, doc.id);
  const response = await fetch(`${heavyDutyMachineryBaseUrl}${url}`, {
    headers: { ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
  });
  if (!response.ok) throw new Error(`Failed to download document (HTTP ${response.status})`);

  const blob = await response.blob();
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = doc.documentType;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};

// =============================================================================
// Dónde está el dinero
//
// One booking's whole money trail, plus the two balances that are not the
// platform's own: guests' fianzas and hosts' deposits. Everything here is
// read-only -- moving money is never a panel action.
// =============================================================================

/** Where a booking's money physically is. Mirrors AdminMoneyLocations on the server. */
export type AdminMoneyLocation =
  | 'NotYetAsked'
  | 'AuthorizedOnCard'
  | 'PlatformEscrow'
  | 'ReleasedToHost'
  | 'Refunded'
  | 'NothingCharged';

export interface IAdminMoneyEvent {
  at: string;
  /** Stable code -- the panel owns the wording, the server never builds a sentence. */
  kind: string;
  amount?: number;
  currency?: string;
  reference?: string;
  detail?: string;
  /** Already scheduled but still ahead of us (a hold expiry, a release date). */
  isForecast: boolean;
}

export interface IAdminBookingDeposit {
  amount?: number;
  status: string;
  /** Hold | SavedCard | None -- what actually stands behind this booking today. */
  coverage: string;
  paymentIntentId?: string;
  authorizationAttemptedAt?: string;
  holdExpiresAt?: string;
  holdExpired: boolean;
  releaseScheduledAt?: string;
  releasedAt?: string;
  authorizationFailed: boolean;
}

export interface IAdminMoneyLine {
  id: string;
  lineType: string;
  title: string;
  providerName: string;
  amount: number;
  hostCurrencyCode: string;
  paymentStatus: string;
  requiresSeparateTransfer: boolean;
  stripeTransferId?: string;
  transferAttempts: number;
  lastTransferAttemptAt?: string;
  transferLastErrorMessage?: string;
}

/** One refund actually given back, with its date. */
export interface IAdminMoneyRefund {
  id: string;
  amount: number;
  currencyCode: string;
  /** Partial | Cancellation | OutOfBand. */
  source: string;
  reference?: string;
  reason?: string;
  issuedByName?: string;
  createdAt: string;
}

export interface IAdminMoneyInvoice {
  id: string;
  invoiceNumber: string;
  type: string;
  kind: string;
  issuerName: string;
  recipientName: string;
  amount: number;
  currency: string;
  issuedAt: string;
}

export interface IAdminBookingMoney {
  bookingId: string;

  /** The booking's reference, e.g. "R-PPTAR" -- the thing a host quotes on the phone asking where their money is. */
  bookingReference: string;

  machineryTitle: string;
  hostName: string;
  guestName: string;
  status: string;
  startAt: string;
  endAt: string;
  createdAt: string;

  captureMode: string;
  paymentStatus: string;
  moneyLocation: AdminMoneyLocation;

  totalPrice: number;
  refundedAmount: number;
  netCharged: number;
  hostPayoutAmount?: number;
  platformFee?: number;
  hostCurrencyCode: string;

  guestChargeAmount: number;
  guestCurrencyCode: string;
  exchangeRate: number;

  paymentReference?: string;
  stripeConnectedAccountId?: string;
  hasSavedCard: boolean;
  paymentAuthorizedAt?: string;
  paymentAuthorizationAttemptedAt?: string;

  fundsReleasedToHostAt?: string;
  fundsReleaseReason?: string;

  deposit: IAdminBookingDeposit;

  overtimeAmountBilled?: number;
  overtimeChargeStatus?: string;
  overtimePaymentIntentId?: string;

  hostDepositCharged: number;

  disputeResolvedAmount?: number;
  disputeSettledAt?: string;
  disputeSettlementError?: string;

  lines: IAdminMoneyLine[];
  linesTotal: number;
  /** False when the lines don't add up to the total -- every proportional split divides by that sum. */
  linesMatchTotal: boolean;

  refunds: IAdminMoneyRefund[];
  /** True when the refunded total doesn't match the refund rows -- money given back before the ledger existed. */
  hasUnloggedRefunds: boolean;

  invoices: IAdminMoneyInvoice[];
  timeline: IAdminMoneyEvent[];
}

export interface IAdminDepositRow {
  bookingId: string;
  machineryTitle: string;
  hostName: string;
  guestName: string;
  amount?: number;
  status: string;
  coverage: string;
  holdExpiresAt?: string;
  holdExpired: boolean;
  releaseScheduledAt?: string;
  releasedAt?: string;
  authorizationFailed: boolean;
  startAt: string;
  endAt: string;
  bookingStatus: string;
}

export interface IAdminDepositSummary {
  heldNow: number;
  heldCount: number;
  expiringSoonCount: number;
  expiredWhileRunningCount: number;
  authorizationFailedCount: number;
  capturedTotal: number;
  capturedCount: number;
}

export interface IAdminHostDepositRow {
  userId: string;
  hostName: string;
  balance: number;
  totalFunded: number;
  currencyCode: string;
  closedAt?: string;
  currentExposure: number;
  maxBookingValue: number;
  isLow: boolean;
  movementCount: number;
  updatedAt: string;
}

export interface IAdminHostDepositMovement {
  id: string;
  amount: number;
  kind: string;
  bookingId?: string;
  paymentReference?: string;
  reason?: string;
  invoiceNumber?: string;
  createdAt: string;
}

export interface IAdminHostDepositDetail extends IAdminHostDepositRow {
  movements: IAdminHostDepositMovement[];
}

export const getAdminBookingMoney = async (bookingId: string): Promise<IAdminBookingMoney> => {
  const response = await apiService<IAdminBookingMoney>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_FINANCE_BOOKING_MONEY(bookingId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the money trail');
};

/** state: held | expiring | expired | failed | captured | released */
export const getAdminDeposits = async (state?: string): Promise<IAdminDepositRow[]> => {
  const response = await apiService<IAdminDepositRow[]>({
    url: `${API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_FINANCE_DEPOSITS}${state ? `?state=${state}` : ''}`,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the deposits');
};

export const getAdminDepositSummary = async (): Promise<IAdminDepositSummary> => {
  const response = await apiService<IAdminDepositSummary>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_FINANCE_DEPOSITS_SUMMARY,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the deposit summary');
};

/** state: low | open | closed */
export const getAdminHostDeposits = async (state?: string): Promise<IAdminHostDepositRow[]> => {
  const response = await apiService<IAdminHostDepositRow[]>({
    url: `${API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_FINANCE_HOST_DEPOSITS}${state ? `?state=${state}` : ''}`,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the host deposits');
};

export const getAdminHostDeposit = async (userId: string): Promise<IAdminHostDepositDetail> => {
  const response = await apiService<IAdminHostDepositDetail>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_FINANCE_HOST_DEPOSIT_BY_ID(userId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the host deposit account');
};

// =============================================================================
// Catálogo: todo lo que no es una máquina
//
// Services, the providers behind them, accessories, transport vehicles and
// insurance policies. Every one of these existed in full on the backend with
// no admin surface at all -- an insurance policy even had a verification
// endpoint nothing could reach, because there was no list to find an id in.
// =============================================================================

export interface IAdminServiceProviderRow {
  userId: string;
  displayName: string;
  fullName: string;
  email: string;
  /** A host's own staff wrapped in the service catalog's shape, not somebody who signed up. */
  isShadowOfHost: boolean;
  suspendedAt?: string;
  suspensionReason?: string;
  serviceCount: number;
  activeServiceCount: number;
  documentCount: number;
  createdAt: string;
}

export interface IAdminServiceRow {
  id: string;
  title: string;
  categoryName: string;
  providerUserId: string;
  providerName: string;
  pricingMode: string;
  hourlyRate?: number;
  fixedJobPrice?: number;
  city?: string;
  countryCode: string;
  active: boolean;
  isShadowOfOperator: boolean;
  /** Active AND its provider not suspended -- computed server-side against the same gate search applies. */
  isBookable: boolean;
  createdAt: string;
}

export interface IAdminAccessoryRow {
  id: string;
  name: string;
  hostUserId: string;
  hostName: string;
  pricePerDay: number;
  quantity: number;
  isActive: boolean;
  isIndependentlyRentable: boolean;
  linkedMachineryCount: number;
  accessoryTypeCode?: string;
  createdAt: string;
}

export interface IAdminTransportVehicleRow {
  id: string;
  name: string;
  licensePlate: string;
  brand?: string;
  hostUserId: string;
  hostName: string;
  fuelConsumptionPer100Km?: number;
  isActive: boolean;
  createdAt: string;
}

export interface IAdminInsurancePolicyRow {
  id: string;
  name: string;
  hostUserId: string;
  hostName: string;
  insurerName?: string;
  policyNumber?: string;
  pricePerDay: number;
  excessAmount?: number;
  coverageLimit?: number;
  expiresOn?: string;
  /** Past its own end date and still on sale unless somebody notices. */
  isExpired: boolean;
  verificationStatus: string;
  reviewedAt?: string;
  hasDocument: boolean;
  isActive: boolean;
  linkedMachineryCount: number;
  createdAt: string;
}

const catalogList = async <T>(url: string, state?: string, search?: string): Promise<T[]> => {
  const params = new URLSearchParams();
  if (state) params.append('state', state);
  if (search) params.append('search', search);
  const query = params.toString();
  const response = await apiService<T[]>({
    url: `${url}${query ? `?${query}` : ''}`,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the catalog');
};

export const getAdminServiceProviders = (state?: string, search?: string) =>
  catalogList<IAdminServiceProviderRow>(API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CATALOG_SERVICE_PROVIDERS, state, search);

export const getAdminServices = (state?: string, search?: string) =>
  catalogList<IAdminServiceRow>(API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CATALOG_SERVICES, state, search);

export const getAdminAccessories = (state?: string, search?: string) =>
  catalogList<IAdminAccessoryRow>(API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CATALOG_ACCESSORIES, state, search);

export const getAdminTransportVehicles = (state?: string, search?: string) =>
  catalogList<IAdminTransportVehicleRow>(API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CATALOG_TRANSPORT_VEHICLES, state, search);

export const getAdminInsurancePolicies = (state?: string, search?: string) =>
  catalogList<IAdminInsurancePolicyRow>(API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CATALOG_INSURANCE_POLICIES, state, search);

export const suspendAdminServiceProvider = async (userId: string, reason: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CATALOG_PROVIDER_SUSPEND(userId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { reason },
  });
  if (!response.success) throw new Error(response.error || 'Failed to suspend the provider');
};

export const reinstateAdminServiceProvider = async (userId: string): Promise<void> => {
  const response = await apiService<{ message: string }>({
    url: API_ENDPOINTS.HEAVY_DUTY_MACHINERY_ADMIN_CATALOG_PROVIDER_REINSTATE(userId),
    method: HTTP_METHODS.POST,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to reinstate the provider');
};

// ===== Comisión por persona (AdminProviderFeeTermsController, Admin-only) =====
// The table only ever holds exceptions, so "no row" is the normal answer and
// not an error -- getProviderFeeTermsForUser returns null for it, same shape
// as getFeatureFlag's own "never set yet".

export const getProviderFeeTermsList = async (): Promise<IProviderFeeTermsList> => {
  const response = await apiService<IProviderFeeTermsList>({
    url: API_ENDPOINTS.ADMIN_PROVIDER_FEE_TERMS,
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to load the commission terms');
};

export const getProviderFeeTermsForUser = async (userId: string): Promise<IProviderFeeTerms | null> => {
  const response = await apiService<IProviderFeeTerms>({
    url: API_ENDPOINTS.ADMIN_PROVIDER_FEE_TERMS_BY_USER(userId),
    method: HTTP_METHODS.GET,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (response.success) return response.data;
  if (response.error?.toLowerCase().includes('standard terms')) return null;
  throw new Error(response.error || 'Failed to load the commission terms');
};

// Upserts -- a payee with no terms yet gets them from this same call.
export const setProviderFeeTerms = async (
  userId: string,
  request: ISetProviderFeeTermsRequest,
): Promise<IProviderFeeTerms> => {
  const response = await apiService<IProviderFeeTerms>({
    url: API_ENDPOINTS.ADMIN_PROVIDER_FEE_TERMS_BY_USER(userId),
    method: HTTP_METHODS.PUT,
    baseUrl: heavyDutyMachineryBaseUrl,
    body: { ...request },
  });
  if (response.success) return response.data;
  throw new Error(response.error || 'Failed to save the commission terms');
};

/** Back to the platform's standard rate. Never touches a booking already taken. */
export const clearProviderFeeTerms = async (userId: string): Promise<void> => {
  const response = await apiService<void>({
    url: API_ENDPOINTS.ADMIN_PROVIDER_FEE_TERMS_BY_USER(userId),
    method: HTTP_METHODS.DELETE,
    baseUrl: heavyDutyMachineryBaseUrl,
  });
  if (!response.success) throw new Error(response.error || 'Failed to clear the commission terms');
};
